import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { motion, AnimatePresence } from "framer-motion";
import { Calculator, LineChart, Users, BookOpen, Sparkles, Leaf, ArrowRight, ChevronRight, ChevronLeft, Home, Factory, Trees, Star, Quote, Send, CheckCircle2, Mail, Phone } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1655300256335-beef51a914fe?w=1200";
const LEAF_IMG = "https://images.unsplash.com/photo-1533038023143-de7a62a9d779?w=800";

const SLIDES = [
  {
    key: "residential",
    icon: Home,
    tag: "Residenziale",
    title: "Casa indipendente ed energetica",
    desc: "Impianti fino a 20 kWp per abitazioni: autoconsumo, batterie di accumulo e detrazione fiscale al 50%.",
    img: "https://images.unsplash.com/photo-1611365892117-00ac5ef43c90?w=1600&q=80&auto=format&fit=crop",
    accent: "from-[#EAB308]/90 to-[#17301C]/70",
  },
  {
    key: "industrial",
    icon: Factory,
    tag: "Industriale",
    title: "Capannoni e aziende in autoproduzione",
    desc: "Fotovoltaico industriale su tetto e pensiline: taglia i costi energetici e ottieni ammortamenti fiscali rapidi.",
    img: "https://images.unsplash.com/photo-1548337138-e87d889cc369?w=1600&q=80&auto=format&fit=crop",
    accent: "from-[#C45B41]/90 to-[#17301C]/70",
  },
  {
    key: "ground",
    icon: Trees,
    tag: "Terreno",
    title: "Grandi impianti a terra e agri-voltaico",
    desc: "Utility scale, agrivoltaico e comunità energetiche: valuta rendimento, PPA e connessione alla rete MT.",
    img: "https://images.unsplash.com/photo-1509391366360-2e959784a276?w=1600&q=80&auto=format&fit=crop",
    accent: "from-[#8BA892]/90 to-[#17301C]/75",
  },
];

function ShowcaseCarousel() {
  const [idx, setIdx] = useState(0);
  const [dir, setDir] = useState(1);
  const total = SLIDES.length;

  useEffect(() => {
    const t = setInterval(() => {
      setDir(1);
      setIdx((p) => (p + 1) % total);
    }, 6000);
    return () => clearInterval(t);
  }, [total]);

  const go = (n) => {
    setDir(n > idx ? 1 : -1);
    setIdx((n + total) % total);
  };
  const next = () => { setDir(1); setIdx((p) => (p + 1) % total); };
  const prev = () => { setDir(-1); setIdx((p) => (p - 1 + total) % total); };

  const slide = SLIDES[idx];
  const Icon = slide.icon;

  return (
    <section className="max-w-7xl mx-auto px-6 pb-24" data-testid="landing-showcase">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="label-eco mb-3">
            <span className="inline-block w-2 h-2 rounded-full bg-[#EAB308] mr-2 align-middle" />
            Le tue soluzioni
          </div>
          <h2 className="text-4xl sm:text-5xl font-black tracking-tight text-[#17301C] leading-tight">
            Un impianto per <span className="italic font-light text-[#EAB308]">ogni scenario</span>
          </h2>
        </div>
        <div className="hidden md:block text-sm text-[#6E7A71] font-mono-nums">
          {String(idx + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
        </div>
      </div>

      <div className="relative rounded-3xl overflow-hidden border-2 border-[#17301C] bg-[#17301C] aspect-[16/9] md:aspect-[21/9]">
        <AnimatePresence initial={false} custom={dir} mode="popLayout">
          <motion.div
            key={slide.key}
            custom={dir}
            initial={{ opacity: 0, x: dir * 60 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -dir * 60 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0"
          >
            <img
              src={slide.img}
              alt={slide.tag}
              className="absolute inset-0 w-full h-full object-cover"
              data-testid={`slide-img-${slide.key}`}
            />
            <div className={`absolute inset-0 bg-gradient-to-tr ${slide.accent}`} />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

            <div className="relative z-10 h-full flex flex-col justify-end p-8 md:p-14">
              <div className="inline-flex items-center gap-2 self-start mb-5 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur-md border border-white/25 text-white">
                <Icon size={16} strokeWidth={2.2} />
                <span className="text-[11px] font-black tracking-[0.18em] uppercase">{slide.tag}</span>
              </div>
              <h3 className="text-3xl sm:text-5xl lg:text-6xl font-black text-white leading-[1.05] max-w-3xl drop-shadow-lg">
                {slide.title}
              </h3>
              <p className="mt-5 text-base sm:text-lg text-white/90 max-w-2xl leading-relaxed">
                {slide.desc}
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link
                  to="/calculator"
                  data-testid={`slide-cta-${slide.key}`}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#EAB308] hover:bg-[#f5bc1b] text-[#17301C] font-black tracking-wide transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                >
                  Calcola ora <ArrowRight size={18} />
                </Link>
                <Link
                  to="/education"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/30 text-white font-semibold transition-all"
                >
                  Scopri di più
                </Link>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Arrows */}
        <button
          onClick={prev}
          aria-label="Precedente"
          data-testid="slide-prev"
          className="hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/15 hover:bg-white/30 backdrop-blur-md border border-white/25 items-center justify-center text-white transition"
        >
          <ChevronLeft size={22} />
        </button>
        <button
          onClick={next}
          aria-label="Successivo"
          data-testid="slide-next"
          className="hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/15 hover:bg-white/30 backdrop-blur-md border border-white/25 items-center justify-center text-white transition"
        >
          <ChevronRight size={22} />
        </button>

        {/* Indicators */}
        <div className="absolute bottom-5 right-6 sm:right-8 z-20 flex items-center gap-2">
          {SLIDES.map((s, i) => (
            <button
              key={s.key}
              onClick={() => go(i)}
              data-testid={`slide-dot-${s.key}`}
              aria-label={`Slide ${i + 1}`}
              className={`transition-all h-2 rounded-full ${i === idx ? "w-8 bg-[#EAB308]" : "w-2 bg-white/50 hover:bg-white/80"}`}
            />
          ))}
        </div>
      </div>

      {/* Thumbnails */}
      <div className="grid grid-cols-3 gap-3 sm:gap-4 mt-4">
        {SLIDES.map((s, i) => {
          const I = s.icon;
          const active = i === idx;
          return (
            <button
              key={s.key}
              onClick={() => go(i)}
              data-testid={`slide-thumb-${s.key}`}
              className={`text-left rounded-2xl p-3 sm:p-4 border-2 transition-all ${active ? "border-[#EAB308] bg-white shadow-md" : "border-transparent bg-[#EAE7E0] hover:border-[#DDE0D8]"}`}
            >
              <div className="flex items-center gap-2 sm:gap-3">
                <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center ${active ? "bg-[#EAB308]" : "bg-white"}`}>
                  <I size={18} className="text-[#17301C]" strokeWidth={2.2} />
                </div>
                <div>
                  <div className="text-[10px] sm:text-xs font-black tracking-widest uppercase text-[#6E7A71]">0{i + 1}</div>
                  <div className="text-sm sm:text-base font-bold text-[#17301C] leading-tight">{s.tag}</div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}

function ContactInfo() {
  const phone = "+39 328 925 9871";
  const phoneRaw = "+393289259871";
  const email = "energiaminniti@gmail.com";
  const whatsappMsg = encodeURIComponent("Ciao, ho visitato solarcontrol-minniti.com e vorrei un preventivo per un impianto fotovoltaico.");

  const cards = [
    {
      key: "phone",
      icon: Phone,
      label: "Chiamaci",
      value: phone,
      href: `tel:${phoneRaw}`,
      cta: "Chiama ora",
      bg: "bg-[#EAB308]",
      fg: "text-[#17301C]",
      testid: "contact-phone-card",
    },
    {
      key: "whatsapp",
      icon: Send,
      label: "Scrivici su WhatsApp",
      value: phone,
      href: `https://wa.me/${phoneRaw.replace("+", "")}?text=${whatsappMsg}`,
      cta: "Apri WhatsApp",
      bg: "bg-[#25D366]",
      fg: "text-white",
      testid: "contact-whatsapp-card",
      external: true,
    },
    {
      key: "email",
      icon: Mail,
      label: "Scrivici una email",
      value: email,
      href: `mailto:${email}?subject=Richiesta%20preventivo%20fotovoltaico`,
      cta: "Manda email",
      bg: "bg-[#17301C]",
      fg: "text-white",
      testid: "contact-email-card",
    },
  ];

  return (
    <section className="max-w-7xl mx-auto px-6 py-24" data-testid="landing-contact">
      <div className="text-center max-w-2xl mx-auto mb-14">
        <div className="inline-flex items-center gap-2 label-eco mb-4">
          <span className="inline-block w-2 h-2 rounded-full bg-[#EAB308] mr-1 align-middle" />
          Contattaci
        </div>
        <h2 className="text-4xl sm:text-5xl font-black tracking-tight text-[#17301C] leading-tight">
          Parla con un <span className="italic font-light text-[#EAB308]">esperto</span>
        </h2>
        <p className="text-lg text-[#3A4A3E] mt-5 leading-relaxed">
          Preventivi, consulenza e sopralluogo <b>gratuiti</b>. Rispondiamo in 24 ore. Scegli come preferisci contattarci.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-5xl mx-auto">
        {cards.map((c, i) => {
          const I = c.icon;
          const isExt = c.external;
          return (
            <motion.a
              key={c.key}
              href={c.href}
              target={isExt ? "_blank" : undefined}
              rel={isExt ? "noopener noreferrer" : undefined}
              data-testid={c.testid}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ delay: i * 0.08 }}
              className={`group relative overflow-hidden rounded-3xl p-8 ${c.bg} ${c.fg} shadow-lg hover:shadow-2xl hover:-translate-y-1 transition-all duration-300`}
            >
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-5 ${c.key === "phone" ? "bg-[#17301C] text-[#EAB308]" : "bg-white/15 backdrop-blur-md"}`}>
                <I size={26} strokeWidth={2.2} />
              </div>
              <div className={`text-xs font-black tracking-widest uppercase mb-2 ${c.key === "phone" ? "text-[#17301C]/70" : "opacity-80"}`}>
                {c.label}
              </div>
              <div className="text-2xl sm:text-[26px] font-black tracking-tight mb-6 font-mono-nums break-all leading-tight">
                {c.value}
              </div>
              <div className={`inline-flex items-center gap-2 text-sm font-black uppercase tracking-widest ${c.key === "phone" ? "text-[#17301C]" : ""}`}>
                {c.cta}
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </div>
              <div className="absolute -bottom-8 -right-8 w-40 h-40 rounded-full bg-white/10 blur-2xl pointer-events-none" />
            </motion.a>
          );
        })}
      </div>

      <div className="mt-10 flex flex-wrap items-center justify-center gap-6 text-sm text-[#6E7A71]">
        <div className="flex items-center gap-2">
          <CheckCircle2 size={16} className="text-[#8BA892]" />
          Preventivo gratuito
        </div>
        <div className="flex items-center gap-2">
          <CheckCircle2 size={16} className="text-[#8BA892]" />
          Sopralluogo senza impegno
        </div>
        <div className="flex items-center gap-2">
          <CheckCircle2 size={16} className="text-[#8BA892]" />
          Risposta entro 24h
        </div>
      </div>
    </section>
  );
}

export default function Landing() {
  const { t } = useApp();

  const features = [
    { key: "calc", icon: Calculator, to: "/calculator", accent: "bg-[#EAB308]" },
    { key: "dash", icon: LineChart, to: "/pricing", accent: "bg-[#8BA892]", pro: true },
    { key: "market", icon: Users, to: "/marketplace", accent: "bg-[#C45B41]" },
    { key: "edu", icon: BookOpen, to: "/education", accent: "bg-[#17301C]" },
    { key: "ai", icon: Sparkles, to: "/calculator", accent: "bg-[#EAB308]" },
    { key: "eco", icon: Leaf, to: "/education", accent: "bg-[#8BA892]" },
  ];

  return (
    <div className="min-h-screen bg-[#F9F8F6] grain-bg">
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 pt-16 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7">
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="label-eco mb-6">
              <span className="inline-block w-2 h-2 rounded-full bg-[#EAB308] mr-2 align-middle" />
              {t.hero.eyebrow}
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter text-[#17301C] leading-[0.95]"
              data-testid="hero-title"
            >
              {t.hero.title_1} <br />
              {t.hero.title_2} <span className="italic font-light text-[#EAB308]">{t.hero.title_3}</span>
            </motion.h1>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="mt-8 text-lg text-[#3A4A3E] max-w-xl leading-relaxed">
              {t.hero.subtitle}
            </motion.p>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="mt-10 flex flex-wrap gap-4">
              <Link to="/calculator" data-testid="hero-cta-calc" className="btn-primary">
                {t.hero.cta_primary} <ArrowRight size={18} />
              </Link>
              <Link to="/education" data-testid="hero-cta-learn" className="btn-secondary">
                {t.hero.cta_secondary}
              </Link>
            </motion.div>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2 }} className="md:col-span-5">
            <div className="relative">
              <div className="aspect-[4/5] rounded-3xl overflow-hidden bg-[#EAE7E0] border-2 border-[#17301C]">
                <img src={HERO_IMG} alt="Solar panels" className="w-full h-full object-cover" />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white border border-[#DDE0D8] rounded-2xl p-4 shadow-lg" data-testid="hero-badge">
                <div className="label-eco text-[#6E7A71]">Media risparmio</div>
                <div className="text-3xl font-black text-[#17301C] font-mono-nums mt-1">€ 1.240/y</div>
              </div>
              <div className="absolute -top-6 -right-6 bg-[#EAB308] rounded-2xl px-4 py-3 shadow-lg rotate-3">
                <div className="text-xs font-bold uppercase tracking-widest text-[#17301C]">CO₂</div>
                <div className="text-2xl font-black text-[#17301C] font-mono-nums">−2.1t/y</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SHOWCASE CAROUSEL */}
      <ShowcaseCarousel />

      {/* FEATURES */}
      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="flex items-end justify-between mb-12">
          <h2 className="text-4xl sm:text-5xl font-black tracking-tight text-[#17301C] max-w-2xl leading-tight">
            {t.features.title}
          </h2>
          <div className="hidden md:block text-sm text-[#6E7A71] font-mono-nums">/06</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
              >
                <Link to={f.to} className="block group" data-testid={`feature-${f.key}`}>
                  <div className="card-surface p-8 h-full flex flex-col relative">
                    {f.pro && (
                      <span className="absolute top-4 right-4 text-[10px] font-black tracking-widest uppercase px-2 py-1 rounded-full bg-[#EAB308] text-[#17301C]">Pro</span>
                    )}
                    <div className={`w-12 h-12 rounded-xl ${f.accent} flex items-center justify-center mb-6`}>
                      <Icon size={22} className={f.key === "edu" ? "text-[#EAB308]" : "text-[#17301C]"} strokeWidth={2.2} />
                    </div>
                    <h3 className="text-2xl font-bold text-[#17301C] mb-2">{t.features[f.key].title}</h3>
                    <p className="text-[#3A4A3E] leading-relaxed">{t.features[f.key].desc}</p>
                    <div className="mt-6 flex items-center gap-1 text-sm font-semibold text-[#17301C] group-hover:gap-2 transition-all">
                      Scopri <ChevronRight size={16} />
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* STATS BAND */}
      <section className="bg-[#17301C] py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { v: "1.450", u: "kWh/kWp", l: "Resa media in Italia" },
              { v: "5-7", u: "anni", l: "Payback tipico" },
              { v: "385g", u: "CO₂/kWh", l: "Risparmiati per ogni kWh" },
              { v: "50%", u: "detrazione", l: "Incentivo fiscale IRPEF" },
            ].map((s, i) => (
              <div key={i} className="text-white">
                <div className="text-5xl font-black font-mono-nums text-[#EAB308]">{s.v}</div>
                <div className="text-xs uppercase tracking-widest text-white/60 mt-2">{s.u}</div>
                <div className="text-sm text-white/90 mt-2">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-7xl mx-auto px-6 py-24" data-testid="landing-testimonials">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="label-eco mb-3">
              <span className="inline-block w-2 h-2 rounded-full bg-[#EAB308] mr-2 align-middle" />
              Recensioni verificate
            </div>
            <h2 className="text-4xl sm:text-5xl font-black tracking-tight text-[#17301C] leading-tight max-w-3xl">
              Cosa dicono i nostri <span className="italic font-light text-[#EAB308]">clienti soddisfatti</span>
            </h2>
          </div>
          <div className="hidden md:flex items-center gap-2">
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={18} className="fill-[#EAB308] text-[#EAB308]" />
              ))}
            </div>
            <div className="text-sm font-mono-nums text-[#17301C] font-bold">4.9/5</div>
            <div className="text-xs text-[#6E7A71]">· 320+ recensioni</div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            {
              name: "Marco Rossi",
              city: "Milano · Residenziale",
              rating: 5,
              text: "Impianto da 6 kWp installato in 2 giorni. Bolletta luce passata da 180€/mese a 35€. Il calcolatore era preciso al 97%!",
              img: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=200&q=80&auto=format&fit=crop",
              savings: "€ 1.740/anno",
            },
            {
              name: "Chiara Bianchi",
              city: "Bologna · Residenziale + Batteria",
              rating: 5,
              text: "Con batteria e app di monitoraggio siamo autosufficienti al 92%. Payback stimato in 5,2 anni. Servizio impeccabile.",
              img: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&q=80&auto=format&fit=crop",
              savings: "€ 2.100/anno",
            },
            {
              name: "Giovanni Esposito",
              city: "Napoli · Capannone industriale",
              rating: 5,
              text: "Impianto da 250 kWp sul tetto del capannone. Ammortizzato in 4 anni grazie alla detrazione e all'export. Consigliatissimo.",
              img: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&q=80&auto=format&fit=crop",
              savings: "€ 48.000/anno",
            },
            {
              name: "Elena Ferrari",
              city: "Torino · PMI",
              rating: 5,
              text: "La dashboard Pro mi permette di monitorare tutti gli inverter da smartphone. Anomalie rilevate in tempo reale, zero fermi impianto.",
              img: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&q=80&auto=format&fit=crop",
              savings: "€ 12.400/anno",
            },
            {
              name: "Luca Marino",
              city: "Bari · Terreno agricolo",
              rating: 5,
              text: "Agrivoltaico da 1 MW su terreno di famiglia. La sezione Bandi mi ha fatto trovare un contributo del 40%. Perfetto.",
              img: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=200&q=80&auto=format&fit=crop",
              savings: "€ 180.000/anno",
            },
            {
              name: "Sofia Greco",
              city: "Palermo · Villa",
              rating: 5,
              text: "L'AI Coach mi ha suggerito il taglio giusto: 8 kWp + pompa di calore. Bolletta gas ridotta del 100%. Team gentile e competente.",
              img: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=200&q=80&auto=format&fit=crop",
              savings: "€ 2.850/anno",
            },
          ].map((r, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ delay: i * 0.06 }}
              className="card-surface p-7 relative flex flex-col"
              data-testid={`testimonial-${i}`}
            >
              <Quote size={28} className="text-[#EAB308] mb-3" strokeWidth={2.4} />
              <div className="flex items-center gap-1 mb-4">
                {[...Array(r.rating)].map((_, j) => (
                  <Star key={j} size={14} className="fill-[#EAB308] text-[#EAB308]" />
                ))}
              </div>
              <p className="text-[#3A4A3E] leading-relaxed mb-6 flex-1">&ldquo;{r.text}&rdquo;</p>
              <div className="flex items-center justify-between gap-3 pt-5 border-t border-[#DDE0D8]">
                <div className="flex items-center gap-3 min-w-0">
                  <img
                    src={r.img}
                    alt={r.name}
                    className="w-11 h-11 rounded-full object-cover border-2 border-[#EAB308] flex-shrink-0"
                  />
                  <div className="min-w-0">
                    <div className="font-bold text-[#17301C] truncate">{r.name}</div>
                    <div className="text-xs text-[#6E7A71] truncate">{r.city}</div>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-[10px] uppercase tracking-widest text-[#6E7A71] font-bold">Risparmio</div>
                  <div className="text-sm font-black text-[#17301C] font-mono-nums">{r.savings}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-10 flex items-center justify-center gap-6 flex-wrap text-sm text-[#6E7A71]">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#8BA892]" />
            Recensioni verificate
          </div>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#EAB308]" />
            320+ impianti installati
          </div>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#C45B41]" />
            Garanzia prodotto 25 anni
          </div>
        </div>
      </section>

      {/* CONTACT INFO */}
      <ContactInfo />

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div className="relative rounded-3xl overflow-hidden">
            <img src={LEAF_IMG} alt="Nature" className="w-full aspect-square object-cover" />
          </div>
          <div>
            <div className="label-eco mb-4">Inizia oggi</div>
            <h2 className="text-4xl sm:text-5xl font-black tracking-tight text-[#17301C] mb-6 leading-tight">
              Pronto a produrre <span className="text-[#EAB308]">energia pulita?</span>
            </h2>
            <p className="text-lg text-[#3A4A3E] mb-8 leading-relaxed">
              Registrati gratis, calcola il risparmio in 60 secondi, ricevi preventivi da installatori verificati.
            </p>
            <Link to="/register" data-testid="cta-register" className="btn-primary">
              Crea account gratis <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#DDE0D8] py-10 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <img src="/logo.jpg" alt="SolarControl Minniti" className="h-10 w-10 object-contain rounded-full bg-white border border-[#DDE0D8]" />
            <div>
              <div className="font-black text-[#17301C]"><span className="text-[#EAB308]">Solar</span>Control <span className="text-xs tracking-widest">MINNITI</span></div>
              <div className="text-xs text-[#6E7A71]">© 2026 · Fotovoltaico per tutti · Made with sun ☀</div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
